import { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import type { Mesh } from "three";

interface WildsChoiceMotesProps {
  center: [number, number, number];
  hue?: number;
  choiceIds: string[];
}

/**
 * Wilds scene setup: small luminous motes orbit the focused tree,
 * one per outgoing choice, each at a distinct drift rate.
 */
export function WildsChoiceMotes({
  center,
  hue = 110,
  choiceIds,
}: WildsChoiceMotesProps) {
  return (
    <group position={center}>
      {choiceIds.map((choiceId, index) => {
        const radius = 1.8 + index * 0.55;
        const speed = 0.32 + index * 0.14;
        const size = 0.12 + (index % 3) * 0.03;
        const hueOffset = index * 22 - 18;
        const phaseOffset = (index / Math.max(choiceIds.length, 1)) * Math.PI * 2;

        return (
          <DriftingMote
            key={choiceId}
            radius={radius}
            speed={speed}
            size={size}
            hue={hue + hueOffset}
            phaseOffset={phaseOffset}
          />
        );
      })}
    </group>
  );
}

function DriftingMote({
  radius,
  speed,
  size,
  hue,
  phaseOffset,
}: {
  radius: number;
  speed: number;
  size: number;
  hue: number;
  phaseOffset: number;
}) {
  const ref = useRef<Mesh>(null);
  const startTime = useRef<number | null>(null);

  useFrame(({ clock }) => {
    if (startTime.current === null) startTime.current = clock.elapsedTime;
    const t = clock.elapsedTime - startTime.current;
    const mesh = ref.current;
    if (!mesh) return;

    const fadeIn = Math.min(1, t / 0.9);
    const angle = clock.elapsedTime * speed + phaseOffset;
    mesh.position.x = Math.cos(angle) * radius;
    mesh.position.z = Math.sin(angle) * radius;
    mesh.position.y = 0.35 + Math.sin(angle * 1.6) * 0.22;
    mesh.scale.setScalar(fadeIn);
  });

  const color = `hsl(${((hue % 360) + 360) % 360}, 78%, 58%)`;

  return (
    <mesh ref={ref} scale={0}>
      <sphereGeometry args={[size, 10, 10]} />
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={0.55}
        roughness={0.35}
        metalness={0.1}
      />
    </mesh>
  );
}
